/**
 * What the crew reported about an item from the crew navigator, on the day of the move.
 *
 * Only set by the navigator's inventory editor — office edits leave it undefined, and an
 * item that was never reviewed has no value here.
 *
 * `missing` is always written together with `quantity: 0`, so every weight/volume/count
 * total drops the item automatically without a single consumer needing to know this field
 * exists. The original `weight` is left intact so the record of what was *expected* on site
 * survives; this field is what distinguishes "the crew could not find it" from "somebody
 * typed zero".
 */
export type InventoryNavStatus = 'missing' | 'added';

export interface InventoryItem {
	id: string;
	navStatus?: InventoryNavStatus;
	isBulky?: boolean;
	isValuable?: boolean;
	isSpecialEquipment?: boolean;
	is3rdPartyServicing?: boolean;
	isProGear?: boolean;
	isAssembleDisassemble?: boolean;
	isCarrierPack?: boolean;
	isoCode1?: string;
	isoCode2?: string;
	isoCode3?: string;
	reviewerName?: string;
	name: string;
	label?: string;
	packing?: string;
	quantity: number;

	volume?: number;
	weight?: number;
	totalVolume?: number;
	totalWeight?: number;

	generated?: boolean;
}

export interface InventoryRoom {
	id: string;

	name: string;
	packing?: any;
	level?: string;
	flights?: string;
	notes?: string;
	moverNotes?: string;
	notMoving?: string;
	isCollapsed?: boolean;

	totalVolume: number;
	totalWeight: number;
	totalItems: number;
	totalBoxes: number;

	inventory: InventoryItem[];
}

export type InventoryItemDefinition = Omit<InventoryItem, 'id' | 'quantity'>;

export const defaultInventory = {
	totalVolume: 0 as number,
	totalItems: 0 as number,
	totalWeight: 0 as number,

	totalBoxes: 0 as number,

	rooms: [] as InventoryRoom[],
	specialtyItems: [] as string[],

	weightUnit: 'lbs',
	volumeUnit: 'ft³',
} as const;

export const jobInventoryInitialState = {
	inventoryEditor: {

		rooms: [] as InventoryRoom[],

		inventorySearch: {} as Record<string, string>,
		inventorySearchResults: [] as InventoryItemDefinition[],
		inventorySearchLoading: false as boolean,

		roomSearch: '' as string,
		roomSearchResults: [] as string[],

		addRoomDialogVisible: false as boolean,
		customRoomName: '' as string,
		specialtyItems: [] as string[],

		touched: false as boolean,

		// Do we actually need to keep these as state? Can we use a selector to resolve them based on the job or zone?
		weightUnit: 'lbs',
		volumeUnit: 'ft³',
	},

	editInventoryItemDialog: {
		selectedItem: undefined as undefined | {
			roomId: string;
			itemId: string;
		},
		name: '' as string,
		quantity: undefined as undefined | number,
		weight: undefined as undefined | number,
		volume: undefined as undefined | number,
	},

	yemboOptions: [] as ('selfSurvey' | 'smartConsult')[],
} as const;


export type InventoryState = typeof jobInventoryInitialState;

export type JobInventory = Pick<InventoryState['inventoryEditor'], 'rooms' | 'specialtyItems' | 'weightUnit' | 'volumeUnit'>;

export type JobInventoryWithTotals = JobInventory & {
	totalItems?: number;
	totalWeight?: number;
	totalVolume?: number;
};
